import * as PetChatbot from '.';
import type { PetChatTime } from '.';
import * as Pet from '../pet';
import type { PetSpeciesID, PetState } from '../pet';
import { SB } from '@play-co/replicant';
import { duration } from '../utils/duration';
import {
  chatbotMessageTemplates,
  generateReplicantChatbotPayload,
} from '../chatbot/messageTemplates';
import { getChatbotFlexMessage } from 'src/replicant/chatbot/flex/utils';
import createScheduledActions, {
  extractScheduledActionHandlers,
  ScheduledActionAPI,
} from 'src/replicant/config/scheduledActions/createScheduledActions';
import { getLocale } from '../getters/user';
import { Feature } from 'src/replicant/analytics';
import { getChatbotMessageChannel } from 'src/replicant/chatbot/chatbot.getters';
import { deliverChatbotMessage } from '../chatbot/chatbot.util';
import * as Fate from '../fate';
import { SyncActionAPI } from '../config/actions/createActions';
import { spawnPetAssets } from '../chatbot/flex/spawnPet';
import { getRandomItemFromArray } from '../utils/random';
import {
  petDefaultGreetingsResponse,
  petImageGreetingsResponse,
} from './petChatbot.greetings';
import { State } from '../State';
import {
  shouldSendStatusMessageUnderRamp,
  incrementStatusMessageRampCounters,
  sendStatusMessageSuppressedAnalytics,
} from './petChatbot.statusMessageRamp';

export const petChatbotScheduledActions = (() => {
  const apiSchema = {
    sendStatusChangedChatbotMessage: SB.object({
      status: SB.tuple(Pet.ruleset.petStates),
      species: SB.tuple(Pet.ruleset.petSpeciesTuple).optional(),
      minutesToMessageSchedule: SB.int().optional(),
    }),
    sendPetChatMessage: SB.tuple(['morning', 'lunch', 'evening']),
    rescheduleStatusChangedChatbotMessage: SB.object({}),
    sendSpawnPetMessage: SB.object({}),
    sendCrateWaitingMessage: SB.object({}),
  };

  const scheduledActions = createScheduledActions(apiSchema)({
    sendStatusChangedChatbotMessage: async (
      state,
      { status, species, minutesToMessageSchedule },
      api,
    ) => {
      const userId = api.getUserID();

      // Schedule the next status update message
      const currentSpecies = Pet.getters.getEquippedPetSpecies(state)!;

      const now = api.date.now();
      const nextPetState = Fate.getters.getNextPetState(state, now);

      scheduleStatusUpdateMessage(
        {
          nextPetState,
          currentSpecies,
          now,
        },
        api,
        userId,
      );

      // Don't send a message if it's disallowed by ruleset logic
      const { canSendPetStatusNotification } = PetChatbot.ruleset;
      const { receiveMessages } = state.settings;
      const channel = getChatbotMessageChannel(
        state,
        { name: 'status', petState: status },
        api.date.now(),
      );
      const shouldSendMessage =
        channel && canSendPetStatusNotification(receiveMessages, status);

      if (!shouldSendMessage) {
        return;
      }

      // Apply status message ramp filtering for new installs A/B test
      const rampResult = shouldSendStatusMessageUnderRamp(state, status, now);

      if (!rampResult.shouldSend) {
        sendStatusMessageSuppressedAnalytics(rampResult, api);
        return;
      }

      const {
        getAnalyticsUserProperties,
        getAnalyticsAttributeUserProperties,
        getPetName,
      } = Pet.getters;
      const payload = generateReplicantChatbotPayload({
        feature: Feature.STATUS._,
        $subFeature: `${Feature.STATUS._}_${status}`,
      });
      payload.senderId = userId;

      const messageSpecies = species ?? currentSpecies;

      if (!messageSpecies) {
        return;
      }

      const message = getChatbotFlexMessage({
        template: 'status',
        payload,
        data: {
          locale: getLocale(state),
          species: messageSpecies,
          petName: getPetName(state),
          status,
        },
        analyticsUserProperties: {
          ...getAnalyticsUserProperties(state, now),
          ...getAnalyticsAttributeUserProperties(state, now),
        },
      });

      const messageSent = deliverChatbotMessage(state, api, {
        receiverId: userId,
        message,
        channel,
      });

      if (messageSent) {
        // Force re-index state for ES
        api.postMessage.offlineUpdateState(userId, {});

        // Update status message ramp counters for new installs A/B test
        incrementStatusMessageRampCounters(
          state,
          now,
          rampResult.incrementTotal,
          rampResult.incrementLimited,
          rampResult.status,
        );
      }

      // Disable help requests for now and do them as a fast followup later
      // const teamId = state.team.currentTeamId;
      // const isStatusForRequest = status === 'sick' || status === 'dying';
      // const shouldSendCareRequest =
      //   state.team.autoCareRequest && isStatusForRequest;
      // if (teamId && shouldSendCareRequest) {
      //   // Circular depencency when importing createSharedStatesActions
      //   (api as any).sharedStates.team.postMessage.sendChatMessageToTeam(
      //     teamId,
      //     {
      //       timestamp: now,
      //       contents: {
      //         type: 'helpRequest',
      //         id: messageSpecies,
      //       },
      //     },
      //   );
      // }
    },

    sendPetChatMessage: async (state, args, api) => {
      const now = api.date.now();
      if (now - state.updatedAt > duration({ days: 1 })) {
        // Stop sending messages 1 day after last login.
        return;
      }

      // Only send chat messages if notifications are fully enabled.
      const channel = getChatbotMessageChannel(
        state,
        { name: 'greetings' },
        api.date.now(),
      );

      if (channel && state.settings.receiveMessages === 'on') {
        const message = petDefaultGreetingsResponse(
          state,
          {
            type: args,
            channel,
            random: api.math.random,
          },
          api,
          now,
        );

        deliverChatbotMessage(state, api, {
          receiverId: api.getUserID(),
          channel,
          message,
        });
      }

      const nextMessage = PetChatbot.ruleset.getNextChatMessageSchedule(
        now,
        api.math.random(),
      );
      schedulePetChatMessage(state, nextMessage, now, api);
    },

    // A scheduled action for manually rescheduling the status OA via CS tool
    rescheduleStatusChangedChatbotMessage: async (state, _, api) => {
      const currentSpecies = Pet.getters.getEquippedPetSpecies(state);
      const now = api.date.now();
      const nextPetState = currentSpecies
        ? Fate.getters.getNextPetState(state, now)
        : null;

      // Bail if next pet state is null or still in crate
      if (!currentSpecies || !nextPetState) {
        return;
      }

      scheduleStatusUpdateMessage(
        {
          nextPetState,
          currentSpecies,
          now,
        },
        api,
      );
    },

    sendSpawnPetMessage: (state, _, api) => {
      // The pet is not in a crate
      if (Pet.getters.getEquippedPetSpecies(state)) {
        return;
      }

      const now = api.date.now();
      const payload = generateReplicantChatbotPayload({
        feature: Feature.GREETING._,
        $subFeature: Feature.GREETING.SPAWN_PET,
      });
      const spawnPetAssetKeys = Object.keys(spawnPetAssets) as Array<
        keyof typeof spawnPetAssets
      >;

      const message = chatbotMessageTemplates.spawnPet({
        payload,
        args: {
          creativeAssetID: getRandomItemFromArray(
            spawnPetAssetKeys,
            api.math.random,
          ),
          locale: getLocale(state),
        },
        analyticsUserProperties: {
          ...Pet.getters.getAnalyticsUserProperties(state, now),
        },
      });

      deliverChatbotMessage(state, api, {
        receiverId: api.getUserID(),
        message,
        channel: 'oa',
      });
    },

    sendCrateWaitingMessage: async () => {
      // NOOP: A/B 0474 resolved to control. Kept registered so queued scheduled
      // actions from older clients/servers drain without failing.
    },
  });

  return {
    schema: apiSchema,
    actions: extractScheduledActionHandlers(scheduledActions),
  };
})();

type ReplicantAPI = SyncActionAPI | ScheduledActionAPI;

export function schedulePetChatMessage(
  state: State,
  message: { period: PetChatTime; delayInMS: number },
  now: number,
  api: ReplicantAPI,
) {
  const { period, delayInMS } = message;
  api.scheduledActions.schedule.sendPetChatMessage({
    args: period,
    notificationId: 'sendPetChatMessage',
    delayInMS,
  });
}

type StatusUpdateScheduleOpts = {
  nextPetState: { state: PetState; timestamp: number } | null;
  currentSpecies: PetSpeciesID;
  now: number;
};

export function scheduleStatusUpdateMessage(
  { nextPetState, now, currentSpecies }: StatusUpdateScheduleOpts,
  api: ReplicantAPI,
  userId: string = api.getUserID(),
) {
  if (!nextPetState) {
    return;
  }

  const { state, timestamp } = nextPetState;

  api.scheduledActions.schedule.sendStatusChangedChatbotMessage({
    args: {
      status: state,
      species: currentSpecies,
      minutesToMessageSchedule: Math.floor(
        (timestamp - now) / duration({ minutes: 1 }),
      ),
    },
    notificationId: 'sendStatusChangedChatbotMessage',
    delayInMS: timestamp - now,
    targetUserId: userId,
  });
}

export function substituteTokens(
  template: string,
  substitutions: Record<string, string>,
) {
  let text = template;
  for (const token in substitutions) {
    text = text.replace(new RegExp(`{${token}}`, 'g'), substitutions[token]);
  }
  return text;
}
